<template>
	<article class="competition-list" v-show="competitions.length > 0">
		<h2>{{ $t('SPORTS_WEBAPP_COMPETITIONS_LIST_HEADLINE') }}</h2>

		<HiddenHorizontalScrollbar @onScrollRight="onLoadNext()">
			<CompetitionCard v-for="competition in competitions" :key="competition.id" :competition="competition" />
		</HiddenHorizontalScrollbar>
	</article>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import CompetitionCard from '@/features/sports/components/allsports/CompetitionCard.vue';
import {
	GetSportCompetitionsDocument,
	GetSportCompetitionsQuery,
	GetSportCompetitionsQueryVariables,
} from '@/features/sports/graphql/queries/GetSportCompetitions.query';
import type { SportPageType } from '@/features/sports/types/interfaces';
import { usePersistedTodaysDate } from '@/helpers/composables/usePersistedTodaysDate';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

defineProps<{
	sportPageType?: SportPageType;
}>();

const { country, language } = storeToRefs(useLanguageStore());
const route = useRoute();
const { todayDate } = usePersistedTodaysDate();
const queryVariables = computed<GetSportCompetitionsQueryVariables>(() => {
	return {
		country: country.value,
		language: language.value,
		first: 50,
		after: '',
		filter: {
			activeOn: todayDate.value.toISOString(),
		},
	};
});

const enabled = computed(() => {
	return route.name === 'app.sports.subdomain.all';
});

const { result, fetchMore } = useQuery<GetSportCompetitionsQuery, GetSportCompetitionsQueryVariables>(
	GetSportCompetitionsDocument,
	queryVariables,
	{
		enabled,
		debounce: 100,
	}
);
const competitionsData = computed(() => result.value?.sportCompetitionsV2);
// For Horizontal Scroll closure
const isReady = ref(true);

const competitions = computed(() => {
	const edges = competitionsData.value?.edges;
	if (edges == null || edges.length === 0) return [];

	return edges.map(competition => competition?.node).filter(Boolean);
});

function onLoadNext() {
	const pageInfo = competitionsData.value?.pageInfo;

	if (!pageInfo?.hasNextPage) return;
	if (!isReady.value) return;

	isReady.value = false;

	fetchMore({
		variables: {
			...queryVariables.value,
			after: pageInfo?.endCursor,
		},
		updateQuery(previousResult, { fetchMoreResult }) {
			const newCompetitions = fetchMoreResult?.sportCompetitionsV2.edges;
			const newPageInfo =
				fetchMoreResult?.sportCompetitionsV2.pageInfo ?? previousResult.sportCompetitionsV2.pageInfo;

			return {
				sportCompetitionsV2: {
					__typename: previousResult.sportCompetitionsV2.__typename,
					totalCount: previousResult.sportCompetitionsV2.totalCount,
					edges: [...(previousResult.sportCompetitionsV2.edges ?? []), ...(newCompetitions ?? [])],
					pageInfo: newPageInfo,
				},
			};
		},
	});
}
</script>

<style lang="scss" scoped>
@import '~@/styles/sports.scss';

.competition-list {
	h2 {
		@include typo-sport-h3;
		color: $color-neutral-neutral-light;
		margin-top: 40px;
		margin-bottom: 20px;

		@media (min-width: 550px) {
			margin-bottom: 32px;
		}
	}
}
</style>
